import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dashboardTypes } from "./dashboardTypes";
import { Container } from "reactstrap";
import {
  getDashboardReadiness,
  getLoggedInUser,
} from "../../helpers/backend_helper";
import {
  createWidget,
  getColumns,
  Icon,
  registry,
  widgetCatalog,
} from "./dashboardRegistry";
import {
  getDashboardData,
  getPersonalDashboard,
  resetPersonalDashboard,
  savePersonalDashboard,
} from "./dashboardApi";
import ResourcesDashboard from "./ResourcesDashboard";
import InspectionOverview from "./InspectionOverview";
import DashboardSetup from "./DashboardSetup";
import { useOnboarding } from "../../hooks/useOnboarding";
import "./InspectionDashboard.scss";

const copy = (value) => JSON.parse(JSON.stringify(value));
const categories = [
  "KPIs",
  "Engagement & Inspection",
  "Risk & Findings",
  "Queries",
  "Workload",
  "Schedule",
];

const sizeLabel = (size) => `${size.charAt(0).toUpperCase()}${size.slice(1)}`;

const dashboardPath = (view) =>
  view === "overview"
    ? "/piers/dashboard"
    : `/piers/dashboard/${view === "reviews" ? "inspections" : view}`;

const Modal = ({ title, children, onClose }) => (
  <div className="dashboard-modal-backdrop" role="presentation">
    <section
      className="dashboard-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        className="dashboard-drawer__close"
        onClick={onClose}
        aria-label="Close dialog"
      >
        <Icon name="bx-x" />
      </button>
      <h2>{title}</h2>
      {children}
    </section>
  </div>
);

const WidgetSettings = ({ widget, onApply, onClose }) => {
  const [configuration, setConfiguration] = useState(
    copy(widget.configuration || {})
  );

  const change = (key, value) =>
    setConfiguration((current) => ({ ...current, [key]: value }));

  const toggle = (key, value) =>
    change(
      key,
      (configuration[key] || []).includes(value)
        ? configuration[key].filter((item) => item !== value)
        : [...(configuration[key] || []), value]
    );
  const title = `${registry[widget.type]?.displayName || "Widget"} Settings`;
  return (
    <aside
      className="dashboard-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <header>
        <h2>{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close settings">
          <Icon name="bx-x" />
        </button>
      </header>
      <div className="dashboard-drawer__body">
        {widget.type === "risk-distribution" && (
          <>
            <fieldset>
              <legend>Risk Levels</legend>
              {[
                ["high", "High"],
                ["medium", "Medium"],
                ["low", "Low"],
                ["notRated", "Not Rated"],
              ].map(([value, label]) => (
                <label className="dashboard-check" key={value}>
                  <input
                    type="checkbox"
                    checked={(configuration.riskLevels || []).includes(value)}
                    onChange={() => toggle("riskLevels", value)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <label className="dashboard-field">
              Inspection Cycle
              <select
                value={configuration.cycle || "current"}
                onChange={(event) => change("cycle", event.target.value)}
              >
                <option value="current">Current Cycle</option>
                <option value="2025">2025 Cycle</option>
                <option value="2026">2026 Cycle</option>
              </select>
            </label>
            <label className="dashboard-field">
              Chart Type
              <select
                value={configuration.chartType || "donut"}
                onChange={(event) => change("chartType", event.target.value)}
              >
                <option value="donut">Donut</option>
                <option value="bar">Bar</option>
              </select>
            </label>
            <label className="dashboard-field">
              Data Scope
              <select
                value={configuration.scope || "accessible"}
                onChange={(event) => change("scope", event.target.value)}
              >
                <option value="accessible">My Accessible Engagements</option>
                <option value="all-accessible">
                  All Accessible Engagements
                </option>
              </select>
            </label>
          </>
        )}
        {widget.type === "upcoming-deadlines" && (
          <>
            <label className="dashboard-field">
              Days Ahead
              <select
                value={configuration.daysAhead || 30}
                onChange={(event) =>
                  change("daysAhead", Number(event.target.value))
                }
              >
                {[7, 14, 30, 60, 90].map((days) => (
                  <option key={days} value={days}>
                    {days}
                  </option>
                ))}
              </select>
            </label>
            <label className="dashboard-switch">
              <span>Include Overdue</span>
              <input
                type="checkbox"
                checked={configuration.includeOverdue !== false}
                onChange={(event) =>
                  change("includeOverdue", event.target.checked)
                }
              />
              <i />
            </label>
            <label className="dashboard-field">
              Maximum Items
              <select
                value={configuration.maxItems || 5}
                onChange={(event) =>
                  change("maxItems", Number(event.target.value))
                }
              >
                {[5, 10, 15].map((items) => (
                  <option key={items} value={items}>
                    {items}
                  </option>
                ))}
              </select>
            </label>
            <label className="dashboard-field">
              Risk Filter
              <select
                value={configuration.riskLevel || "all"}
                onChange={(event) => change("riskLevel", event.target.value)}
              >
                {["all", "high", "medium", "low"].map((level) => (
                  <option key={level} value={level}>
                    {level === "all" ? "All" : sizeLabel(level)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        {widget.type === "query-analytics" && (
          <>
            <fieldset>
              <legend>Aging Buckets</legend>
              {[
                ["0-7", "0–7 days"],
                ["8-14", "8–14 days"],
                ["15-30", "15–30 days"],
                ["30+", "> 30 days"],
              ].map(([value, label]) => (
                <label className="dashboard-check" key={value}>
                  <input
                    type="checkbox"
                    checked={(configuration.agingBuckets || []).includes(value)}
                    onChange={() => toggle("agingBuckets", value)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <label className="dashboard-switch">
              <span>Display Response Time</span>
              <input
                type="checkbox"
                checked={configuration.displayResponseTime !== false}
                onChange={(event) =>
                  change("displayResponseTime", event.target.checked)
                }
              />
              <i />
            </label>
            <label className="dashboard-switch">
              <span>Display Query Aging</span>
              <input
                type="checkbox"
                checked={configuration.displayQueryAging !== false}
                onChange={(event) =>
                  change("displayQueryAging", event.target.checked)
                }
              />
              <i />
            </label>
          </>
        )}
      </div>
      <footer>
        <button type="button" className="ops-button" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ops-button ops-button--primary"
          onClick={() => onApply(configuration)}
        >
          Apply
        </button>
      </footer>
    </aside>
  );
};

const AddWidgetDrawer = ({ widgets, onAdd, onClose }) => {
  const [query, setQuery] = useState("");
  const visible = widgetCatalog.filter(
    (item) =>
      !query.trim() ||
      `${item.displayName} ${item.category} ${item.description} ${item.type}`
        .toLowerCase()
        .includes(query.trim().toLowerCase())
  );
  return (
    <aside
      className="dashboard-drawer dashboard-drawer--catalog"
      role="dialog"
      aria-modal="true"
      aria-label="Add widget"
    >
      <header>
        <h2>ADD WIDGET</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close widget library"
        >
          <Icon name="bx-x" />
        </button>
      </header>
      <div className="dashboard-drawer__body">
        <label className="dashboard-search">
          <Icon name="bx-search" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search widgets..."
            aria-label="Search widgets"
          />
        </label>
        {categories.map((category) => {
          const items = visible.filter((item) => item.category === category);
          return items.length ? (
            <section className="dashboard-library" key={category}>
              <h3>{category}</h3>
              {items.map((item) => {
                const added = widgets.some(
                  (widget) => widget.type === item.type
                );
                return (
                  <div key={item.type}>
                    <span>
                      <i>
                        <Icon name={item.icon} />
                      </i>
                      <b>{item.displayName}</b>
                    </span>
                    <button
                      type="button"
                      disabled={added}
                      onClick={() => onAdd(item.type)}
                    >
                      {added ? "Added" : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </section>
          ) : null;
        })}
      </div>
      <footer className="dashboard-browse">
        Browse all widgets <Icon name="bx-right-arrow-alt" />
      </footer>
    </aside>
  );
};

const DashboardWidget = ({
  widget,
  data,
  customize,
  onMove,
  onSize,
  onSettings,
  onRemove,
}) => {
  const definition = registry[widget.type];
  if (!definition) return null;
  const Component = definition.Component;
  const supportedSizes = Object.keys(definition.supportedSizes).filter(
    (size, index, values) =>
      definition.supportedSizes[size] && values.indexOf(size) === index
  );
  return (
    <article
      className={`dashboard-widget ${
        customize ? "dashboard-widget--editing" : ""
      }`}
      style={{ "--dashboard-columns": getColumns(widget) }}
      draggable={customize}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", widget.id);
      }}
      onDragOver={(event) => customize && event.preventDefault()}
      onDrop={(event) => {
        if (!customize) return;
        event.preventDefault();
        const id = event.dataTransfer.getData("text/plain");
        if (id && id !== widget.id) onMove(id, widget.id);
      }}
    >
      {customize && (
        <div className="dashboard-widget__controls">
          <button
            type="button"
            className="dashboard-grip"
            aria-label={`Move ${definition.displayName}`}
            title="Drag to move"
          >
            <Icon name="bx-grid-vertical" />
          </button>
          <div className="dashboard-widget__tools">
            {supportedSizes.length > 1 && (
              <select
                value={widget.size}
                onChange={(event) => onSize(widget.id, event.target.value)}
                aria-label={`Change ${definition.displayName} size`}
              >
                {supportedSizes.map((size) => (
                  <option key={size} value={size}>
                    {sizeLabel(size)}
                  </option>
                ))}
              </select>
            )}
            {definition.configurable && (
              <button
                type="button"
                onClick={() => onSettings(widget)}
                aria-label={`Configure ${definition.displayName}`}
                title="Widget settings"
              >
                <Icon name="bx-cog" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onRemove(widget)}
              aria-label={`Remove ${definition.displayName}`}
              title="Remove widget"
            >
              <Icon name="bx-x" />
            </button>
          </div>
        </div>
      )}
      <Component widget={widget} data={data} />
      {customize && (
        <div className="dashboard-widget__mobile-move">
          <button type="button" onClick={() => onMove(widget.id, "previous")}>
            Move Up
          </button>
          <button type="button" onClick={() => onMove(widget.id, "next")}>
            Move Down
          </button>
        </div>
      )}
    </article>
  );
};

const InspectionDashboard = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    gettingStarted,
    gettingStartedLoading,
    gettingStartedError,
    onboardingSummary,
    launchAction,
  } = useOnboarding();
  const [busy, setBusy] = useState(false);
  const view =
    dashboardTypes.find((item) => dashboardPath(item.id) === pathname)?.id ||
    "overview";
  const selectedDashboard = dashboardTypes.find((item) => item.id === view);
  const [readiness, setReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState("");
  const [readinessRequest, setReadinessRequest] = useState(0);
  const [dashboardData, setDashboardData] = useState(null);
  const [dataError, setDataError] = useState("");
  const personalJourney = gettingStarted?.journey === "personal";
  const gettingStartedPending = gettingStartedLoading && !gettingStarted;
  const personalJourneyComplete =
    onboardingSummary.totalCount > 0 &&
    onboardingSummary.completedCount === onboardingSummary.totalCount;
  const firmWorkspaceReady =
    readiness &&
    ["inspectionCycles", "engagements", "inspections"].every(
      (key) => Number(readiness[key]) > 0
    );
  const readyForDashboard =
    !gettingStartedPending &&
    (personalJourney ? (pathname === "/inspections/overview" || personalJourneyComplete) : firmWorkspaceReady);

  useEffect(() => {
    let active = true;
    setReadiness(null);
    setReadinessError("");
    getDashboardReadiness()
      .then((response) => {
        if (!response?.status || !response.data)
          throw new Error(
            response?.message || "Unable to check inspection activity."
          );
        if (active) setReadiness(response.data);
      })
      .catch((error) => {
        if (active)
          setReadinessError(
            error.message || "Unable to check inspection activity."
          );
      });
    return () => {
      active = false;
    };
  }, [readinessRequest]);

  useEffect(() => {
    if (!readyForDashboard) return;
    let active = true;
    setDataError("");
    getDashboardData()
      .then((data) => {
        if (active) setDashboardData(data);
      })
      .catch((error) => {
        if (active)
          setDataError(error.message || "Unable to load dashboard data.");
      });
    return () => {
      active = false;
    };
  }, [readyForDashboard, readinessRequest]);

  const [userId, setUserId] = useState();
  const [widgets, setWidgets] = useState([]);
  const [savedWidgets, setSavedWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customize, setCustomize] = useState(false);
  const [addDrawer, setAddDrawer] = useState(false);
  const [settingsWidget, setSettingsWidget] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [notice, setNotice] = useState("");
  const dirty = JSON.stringify(widgets) !== JSON.stringify(savedWidgets);
  useEffect(() => {
    if (!readyForDashboard || view === "resources") {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getLoggedInUser()
      .then(async (user) => {
        const id = user?.id || user?.userId;
        const loaded = await getPersonalDashboard(id, view);
        if (active) {
          setUserId(id);
          setWidgets(loaded);
          setSavedWidgets(copy(loaded));
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setLoading(false);
          setNotice(
            "Unable to load dashboard preferences. Please reload and try again."
          );
        }
      });
    return () => {
      active = false;
    };
  }, [view, readyForDashboard]);

  const switchDashboard = (next) => {
    if (next === view || busy) return;

    const change = () => {
      navigate(dashboardPath(next));
      setCustomize(false);
      setAddDrawer(false);
      setSettingsWidget(null);
      setNotice("");
    };
    if (customize && dirty)
      setConfirm({
        title: "Discard dashboard changes?",
        message:
          "Save your changes before switching, or discard them to open the selected dashboard.",
        confirm: "Discard & Switch",
        action: change,
      });
    else change();
  };
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setAddDrawer(false);
        setSettingsWidget(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);
  useEffect(() => {
    const beforeUnload = (event) => {
      if (customize && dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [customize, dirty]);

  const updateWidgets = (update) =>
    setWidgets((current) =>
      update(current).map((widget, index) => ({ ...widget, sortOrder: index }))
    );

  const moveWidget = (sourceId, target) =>
    updateWidgets((current) => {
      const source = current.findIndex((widget) => widget.id === sourceId);
      if (source < 0) return current;
      const destination =
        target === "previous"
          ? source - 1
          : target === "next"
          ? source + 1
          : current.findIndex((widget) => widget.id === target);
      if (destination < 0 || destination >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(source, 1);
      next.splice(destination, 0, moved);
      return next;
    });

  const cancel = () => {
    if (dirty)
      setConfirm({
        title: "Discard dashboard changes?",
        message: "Your unsaved dashboard changes will be lost.",
        confirm: "Discard Changes",
        action: () => {
          setWidgets(copy(savedWidgets));
          setCustomize(false);
        },
      });
    else setCustomize(false);
  };

  const reset = () =>
    setConfirm({
      title: "Reset dashboard?",
      message:
        "Your personal dashboard layout and widget settings will be replaced with the default layout for this view.",
      confirm: "Reset Dashboard",
      action: async () => {
        setBusy(true);
        try {
          const defaults = await resetPersonalDashboard(userId, view);
          setWidgets(defaults);
          setSavedWidgets(copy(defaults));
          setCustomize(false);
          setNotice("Dashboard reset to its default layout.");
        } catch (_) {
          setNotice("Unable to reset this dashboard. Please try again.");
        } finally {
          setBusy(false);
        }
      },
    });
  const sortedWidgets = useMemo(
    () => [...widgets].sort((a, b) => a.sortOrder - b.sortOrder),
    [widgets]
  );
  const sidebar = pathname === "/inspections/overview" ? null : (
    <nav className="dashboard-view-sidebar" aria-label="Dashboard views">
      <div>
        {dashboardTypes.map((item) => (
          <button
            type="button"
            key={item.id}
            disabled={busy}
            onClick={() => switchDashboard(item.id)}
            className={view === item.id ? "is-active" : ""}
            aria-current={view === item.id ? "page" : undefined}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
      <small>
        PIERS<strong>v1.0</strong>
      </small>
    </nav>
  );
  if (!readyForDashboard || (!personalJourney && readinessError))
    return (
      <main className={(`ops-dashboard ops-dashboard--${view}`) + " piers-page"}>
        {sidebar}
        <DashboardSetup
          counts={gettingStartedPending ? null : readiness}
          summary={personalJourney ? onboardingSummary : null}
          error={personalJourney ? gettingStartedError : readinessError}
          onRetry={() => setReadinessRequest((value) => value + 1)}
          onNavigate={navigate}
          onLaunch={launchAction}
        />
      </main>
    );
  if (pathname === "/inspections/overview")
    return <InspectionOverview data={dashboardData} loading={!dashboardData && !dataError} error={dataError} onRetry={() => setReadinessRequest((value) => value + 1)} />;
  if (view === "resources")
    return (
      <main className="ops-dashboard ops-dashboard--resources piers-page">
        {sidebar}
        <ResourcesDashboard
          data={dashboardData?.resources}
          loading={!dashboardData && !dataError}
          error={dataError}
        />
      </main>
    );
  return (
    <main
      className={(`ops-dashboard ops-dashboard--${view} ${
        customize ? "ops-dashboard--customizing" : ""
      }`) + " piers-page"}
    >
      <nav className="dashboard-view-sidebar" aria-label="Dashboard views">
        <div>
          {dashboardTypes.map((item) => (
            <button
              type="button"
              key={item.id}
              disabled={busy}
              onClick={() => switchDashboard(item.id)}
              className={view === item.id ? "is-active" : ""}
              aria-current={view === item.id ? "page" : undefined}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        <small>
          PIERS<strong>v1.0</strong>
        </small>
      </nav>
      <Container fluid>
        <header className="ops-dashboard__intro dashboard-page-header">
          <div className="piers-page-header">
            <h1 className="piers-page-title">{selectedDashboard.title || selectedDashboard.label}</h1>
            <span>{selectedDashboard.description}</span>
          </div>
          <em className="dashboard-page-header__motto">
            {selectedDashboard.motto}
          </em>
          <div className="ops-dashboard__actions">
            <button
              type="button"
              className="ops-button"
              disabled={loading || busy}
              onClick={() => (customize ? cancel() : setCustomize(true))}
            >
              <Icon name="bx-cog" />{" "}
              {customize ? "Exit Customize" : "Customize Dashboard"}
            </button>
          </div>
        </header>
        {dataError && (
          <div className="dashboard-notice" role="alert">
            <Icon name="bx-error-circle" />
            {dataError}
          </div>
        )}
        {customize && (
          <section className="dashboard-toolbar">
            <button
              type="button"
              className="ops-button ops-button--primary"
              onClick={() => setAddDrawer(true)}
            >
              <Icon name="bx-plus" /> Add Widget
            </button>
            <button
              type="button"
              className="ops-button"
              disabled={busy}
              onClick={reset}
            >
              <Icon name="bx-reset" /> Reset to Default
            </button>
            <span />
            <button type="button" className="ops-button" onClick={cancel}>
              Cancel
            </button>
            <button
              type="button"
              className="ops-button ops-button--primary"
              disabled={busy}
              onClick={async () => {
                if (!widgets.length)
                  return setNotice(
                    "Add at least one widget before saving your dashboard."
                  );
                setBusy(true);
                try {
                  await savePersonalDashboard(userId, widgets, view);
                  setSavedWidgets(copy(widgets));
                  setCustomize(false);
                  setNotice("Dashboard layout saved.");
                } catch (_) {
                  setNotice("Unable to save this dashboard. Please try again.");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save Dashboard
            </button>
          </section>
        )}
        {loading || (!dashboardData && !dataError) ? (
          <section className="dashboard-grid dashboard-grid--loading">
            {Array.from({ length: 9 }).map((_, index) => (
              <div className="dashboard-skeleton" key={index} />
            ))}
          </section>
        ) : sortedWidgets.length ? (
          <section className="dashboard-grid">
            {sortedWidgets.map((widget) => (
              <DashboardWidget
                key={widget.id}
                widget={widget}
                data={
                  view === "reviews"
                    ? dashboardData?.reviews
                    : view === "my-work"
                    ? dashboardData?.myWork
                    : dashboardData?.overview
                }
                customize={customize}
                onMove={moveWidget}
                onSize={(id, size) =>
                  updateWidgets((current) =>
                    current.map((item) =>
                      item.id === id ? { ...item, size } : item
                    )
                  )
                }
                onSettings={setSettingsWidget}
                onRemove={(item) =>
                  setConfirm({
                    title: `Remove ${
                      registry[item.type]?.displayName || "this widget"
                    } from your dashboard?`,
                    message:
                      "This change will be saved only when you save your dashboard.",
                    confirm: "Remove",
                    action: () =>
                      updateWidgets((current) =>
                        current.filter((widget) => widget.id !== item.id)
                      ),
                  })
                }
              />
            ))}
          </section>
        ) : (
          <section className="dashboard-empty">
            <Icon name="bx-layout" />
            <h2>Your dashboard is empty.</h2>
            <p>Add widgets to create a dashboard that matches how you work.</p>
            <button
              type="button"
              className="ops-button ops-button--primary"
              onClick={() => {
                setCustomize(true);
                setAddDrawer(true);
              }}
            >
              <Icon name="bx-plus" /> Add Widget
            </button>
            <button
              type="button"
              className="ops-button"
              disabled={busy}
              onClick={reset}
            >
              Reset to Default
            </button>
          </section>
        )}
      </Container>
      {addDrawer && (
        <AddWidgetDrawer
          widgets={widgets}
          onAdd={(type) =>
            updateWidgets((current) => [
              ...current,
              createWidget(type, current.length),
            ])
          }
          onClose={() => setAddDrawer(false)}
        />
      )}
      {settingsWidget && (
        <WidgetSettings
          widget={settingsWidget}
          onApply={(configuration) => {
            updateWidgets((current) =>
              current.map((widget) =>
                widget.id === settingsWidget.id
                  ? { ...widget, configuration }
                  : widget
              )
            );
            setSettingsWidget(null);
          }}
          onClose={() => setSettingsWidget(null)}
        />
      )}
      {confirm && (
        <Modal title={confirm.title} onClose={() => setConfirm(null)}>
          <p>{confirm.message}</p>
          <footer>
            <button
              type="button"
              className="ops-button"
              onClick={() => setConfirm(null)}
            >
              {confirm.title.startsWith("Discard")
                ? "Continue Editing"
                : "Cancel"}
            </button>
            <button
              type="button"
              className="ops-button ops-button--primary"
              onClick={() => {
                confirm.action();
                setConfirm(null);
              }}
            >
              {confirm.confirm}
            </button>
          </footer>
        </Modal>
      )}
      {notice && (
        <div className="dashboard-notice" role="status">
          <Icon name="bx-check-circle" />
          {notice}
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => setNotice("")}
          >
            <Icon name="bx-x" />
          </button>
        </div>
      )}
    </main>
  );
};

export default InspectionDashboard;
